import React, { useState, useEffect } from 'react';
import { getTasks, createTask, updateTaskStatus, updateTaskDates, getUsers, addTaskComment, deleteTask } from '../lib/api';
import { formatDateTime, statusLabels, getStatusClass, priorityLabels, getPriorityClass } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, CheckSquare, Clock, AlertTriangle, CheckCircle, Loader2,
  User, MessageSquare, Send, ChevronDown, ChevronUp, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const Tareas = () => {
  const { user: currentUser, isDirection } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pendiente');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [editingDates, setEditingDates] = useState(null);
  const [editDates, setEditDates] = useState({ due_date: '', due_date_end: '' });
  const [formData, setFormData] = useState({
    title: '', description: '', priority: 'media', due_date: '', due_date_end: '', assigned_to: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes] = await Promise.all([
        getTasks(),
        isDirection() ? getUsers() : Promise.resolve({ data: [] })
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      
    } finally { setLoading(false); }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createTask({
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        due_date_end: formData.due_date_end ? new Date(formData.due_date_end).toISOString() : null,
        assigned_to: formData.assigned_to || null
      });
      toast.success('Tarea creada');
      setShowNewDialog(false);
      setFormData({ title: '', description: '', priority: 'media', due_date: '', due_date_end: '', assigned_to: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear tarea');
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTaskStatus(taskId, newStatus);
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al actualizar tarea';
      toast.error(detail);
    }
  };

  const openEditDates = (task) => {
    const toLocal = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    };
    setEditDates({ due_date: toLocal(task.due_date), due_date_end: toLocal(task.due_date_end) });
    setEditingDates(task.id);
  };

  const saveEditDates = async () => {
    try {
      const payload = {
        due_date: editDates.due_date ? new Date(editDates.due_date).toISOString() : null,
        due_date_end: editDates.due_date_end ? new Date(editDates.due_date_end).toISOString() : null,
        reactivate: true
      };
      await updateTaskDates(editingDates, payload);
      toast.success('Fechas actualizadas');
      setEditingDates(null);
      fetchData();
    } catch (e) {
      toast.error('Error al actualizar fechas');
    }
  };

  const handleAddComment = async (taskId) => {
    if (!newComment.trim()) return;
    setSavingComment(true);
    try {
      await addTaskComment(taskId, newComment.trim());
      setNewComment('');
      toast.success('Nota agregada');
      fetchData();
    } catch (error) {
      toast.error('Error al agregar nota');
    } finally { setSavingComment(false); }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Eliminar tarea "${task.title}"? Esta accion no se puede deshacer.`)) return;
    try {
      await deleteTask(task.id);
      toast.success('Tarea eliminada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const toggleExpand = (taskId) => {
    setExpandedTask(expandedTask === taskId ? null : taskId);
    setNewComment('');
  };

  const filterTasksByStatus = (status) => {
    if (status === 'vencida') {
      return tasks.filter(t => t.status === 'vencida' || 
        (t.status === 'pendiente' && t.due_date && new Date(t.due_date) < new Date()));
    }
    return tasks.filter(t => t.status === status);
  };

  const getTaskIcon = (status) => {
    switch (status) {
      case 'hecho': return <CheckCircle className="text-[#4A7C59]" size={18} />;
      case 'vencida': return <AlertTriangle className="text-[#C25953]" size={18} />;
      case 'en_curso': return <Clock className="text-[#D98A6C]" size={18} />;
      default: return <CheckSquare className="text-[#5C7083]" size={18} />;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="tasks-loading"><Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" /></div>;
  }

  const pendingCount = filterTasksByStatus('pendiente').length;
  const inProgressCount = filterTasksByStatus('en_curso').length;
  const overdueCount = filterTasksByStatus('vencida').length;
  const doneCount = filterTasksByStatus('hecho').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Tareas y Pendientes</h1>
          <p className="page-subtitle">Gestion de tareas del equipo</p>
        </div>
        <Button className="btn-primary gap-2" onClick={() => setShowNewDialog(true)} data-testid="btn-new-task">
          <Plus size={18} /> Nueva Tarea
        </Button>
      </div>

      {/* Assignee Filter */}
      {users.length > 0 && (
        <div className="card py-3 px-4">
          <div className="flex items-center gap-3">
            <User size={16} className="text-[#5C7083]" />
            <Select value={assigneeFilter || "all"} onValueChange={(v) => setAssigneeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px]" data-testid="select-assignee-filter">
                <SelectValue placeholder="Filtrar por persona" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {assigneeFilter && (
              <Button variant="ghost" size="sm" className="btn-ghost text-xs" onClick={() => setAssigneeFilter('')}>
                Limpiar filtro
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#F5F0E8] rounded-lg"><CheckSquare className="text-[#5C7083]" size={24} /></div>
          <div><p className="text-2xl font-semibold">{pendingCount}</p><p className="text-sm text-[#5C7083]">Pendientes</p></div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#FAF0EC] rounded-lg"><Clock className="text-[#D98A6C]" size={24} /></div>
          <div><p className="text-2xl font-semibold">{inProgressCount}</p><p className="text-sm text-[#5C7083]">En Curso</p></div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#FBEAE9] rounded-lg"><AlertTriangle className="text-[#C25953]" size={24} /></div>
          <div><p className="text-2xl font-semibold">{overdueCount}</p><p className="text-sm text-[#5C7083]">Vencidas</p></div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#EDF5F0] rounded-lg"><CheckCircle className="text-[#4A7C59]" size={24} /></div>
          <div><p className="text-2xl font-semibold">{doneCount}</p><p className="text-sm text-[#5C7083]">Completadas</p></div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-[#E8E2D7] p-1 rounded-lg" data-testid="tasks-tabs">
          <TabsTrigger value="pendiente" className="data-[state=active]:bg-[#2B4257] data-[state=active]:text-white rounded-md px-4">
            Pendientes ({pendingCount})
          </TabsTrigger>
          <TabsTrigger value="en_curso" className="data-[state=active]:bg-[#2B4257] data-[state=active]:text-white rounded-md px-4">
            En Curso ({inProgressCount})
          </TabsTrigger>
          <TabsTrigger value="vencida" className="data-[state=active]:bg-[#2B4257] data-[state=active]:text-white rounded-md px-4">
            Vencidas ({overdueCount})
          </TabsTrigger>
          <TabsTrigger value="hecho" className="data-[state=active]:bg-[#2B4257] data-[state=active]:text-white rounded-md px-4">
            Completadas
          </TabsTrigger>
        </TabsList>

        {['pendiente', 'en_curso', 'vencida', 'hecho'].map((status) => (
          <TabsContent key={status} value={status} className="mt-4">
            <div className="space-y-3" data-testid={`tasks-list-${status}`}>
              {filterTasksByStatus(status).length === 0 ? (
                <div className="card text-center py-12">
                  <CheckSquare className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
                  <p className="text-[#5C7083]">No hay tareas {statusLabels[status]?.toLowerCase()}</p>
                </div>
              ) : (
                filterTasksByStatus(status).map((task) => (
                  <div key={task.id} className="card hover:border-[#2B4257] transition-colors" data-testid={`task-item-${task.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="mt-1">{getTaskIcon(task.status)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-medium text-[#1A2835]">{task.title}</h4>
                            {task.description && <p className="text-sm text-[#5C7083] mt-1">{task.description}</p>}
                          </div>
                          <span className={`status-badge ${getPriorityClass(task.priority)} flex-shrink-0`}>
                            {priorityLabels[task.priority]}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-[#5C7083]">
                          {task.due_date && (
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatDateTime(task.due_date)}
                              {task.due_date_end && (<> — {formatDateTime(task.due_date_end)}</>)}
                            </span>
                          )}
                          {task.assigned_to_name && (
                            <span className="flex items-center gap-1"><User size={14} />{task.assigned_to_name}</span>
                          )}
                          {task.task_type && (
                            <span className="status-badge badge-default">
                              {task.task_type === 'lead_followup' ? 'Seguimiento Lead' : task.task_type === 'rejected_payment' ? 'Pago Rechazado' : task.task_type}
                            </span>
                          )}
                          <button
                            onClick={() => toggleExpand(task.id)}
                            className="flex items-center gap-1 text-[#2B4257] hover:text-[#1A2835] transition-colors cursor-pointer"
                            data-testid={`btn-notes-${task.id}`}
                          >
                            <MessageSquare size={14} />
                            <span className="text-xs font-medium">{task.comments?.length || 0} notas</span>
                            {expandedTask === task.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        </div>
                      </div>
                      {task.status !== 'hecho' && (
                        <div className="flex gap-2 flex-shrink-0">
                          {task.status === 'pendiente' && (
                            <Button variant="ghost" size="sm" className="btn-ghost" onClick={() => handleStatusChange(task.id, 'en_curso')} data-testid={`btn-start-${task.id}`}>
                              Iniciar
                            </Button>
                          )}
                          <Button variant="ghost" size="sm"
                            className={`btn-ghost text-[#4A7C59] ${task.priority === 'alta' && !isDirection() ? 'opacity-50' : ''}`}
                            onClick={() => handleStatusChange(task.id, 'hecho')}
                            data-testid={`btn-complete-${task.id}`}
                            title={task.priority === 'alta' && !isDirection() ? 'Solo Direccion puede cerrar tareas Alta' : 'Completar'}
                          >
                            Completar
                          </Button>
                          {isDirection() && (
                            <Button variant="ghost" size="sm" className="btn-ghost text-[#2B4257]"
                              onClick={() => openEditDates(task)} data-testid={`btn-edit-dates-${task.id}`}>
                              <Clock size={14} className="mr-1" /> Fechas
                            </Button>
                          )}
                          {isDirection() && (
                            <Button variant="ghost" size="sm"
                              className="btn-ghost p-2 text-[#C25953] hover:text-[#A63D3D] hover:bg-[#FBEAE9]"
                              onClick={() => handleDeleteTask(task)}
                              data-testid={`btn-delete-task-${task.id}`}>
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      )}
                      {task.status === 'hecho' && isDirection() && (
                        <div className="flex gap-2 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="btn-ghost text-[#2B4257]"
                            onClick={() => openEditDates(task)} data-testid={`btn-edit-dates-done-${task.id}`}>
                            <Clock size={14} className="mr-1" /> Fechas
                          </Button>
                          <Button variant="ghost" size="sm"
                            className="btn-ghost p-2 text-[#C25953] hover:text-[#A63D3D] hover:bg-[#FBEAE9]"
                            onClick={() => handleDeleteTask(task)}
                            data-testid={`btn-delete-done-${task.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Edit Dates Inline (Direction only) */}
                    {editingDates === task.id && (
                      <div className="mt-3 p-3 bg-[#F5F0E8] rounded-lg" data-testid={`edit-dates-${task.id}`}>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <Label className="text-xs text-[#5C7083]">Fecha Inicio</Label>
                            <Input type="datetime-local" value={editDates.due_date}
                              onChange={e => setEditDates({ ...editDates, due_date: e.target.value })}
                              className="text-sm h-8" data-testid="edit-due-date" />
                          </div>
                          <div>
                            <Label className="text-xs text-[#5C7083]">Fecha Limite</Label>
                            <Input type="datetime-local" value={editDates.due_date_end}
                              onChange={e => setEditDates({ ...editDates, due_date_end: e.target.value })}
                              className="text-sm h-8" data-testid="edit-due-date-end" />
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" size="sm" className="btn-secondary text-xs" onClick={() => setEditingDates(null)}>Cancelar</Button>
                          <Button size="sm" className="btn-primary text-xs" onClick={saveEditDates} data-testid="btn-save-dates">Guardar</Button>
                        </div>
                      </div>
                    )}

                    {/* Expanded Notes Section */}
                    {expandedTask === task.id && (
                      <div className="mt-4 pt-4 border-t border-[#E8E2D7]" data-testid={`task-notes-${task.id}`}>
                        {/* Existing comments */}
                        {task.comments?.length > 0 ? (
                          <div className="space-y-3 mb-4">
                            {task.comments.map((c) => (
                              <div key={c.id} className="flex gap-3 p-3 bg-[#F9F7F3] rounded-lg">
                                <div className="w-7 h-7 rounded-full bg-[#2B4257] flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-xs font-medium">{(c.user_name || '?')[0]}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-[#1A2835]">{c.user_name}</span>
                                    <span className="text-[10px] text-[#5C7083]">{formatDateTime(c.created_at)}</span>
                                  </div>
                                  <p className="text-sm text-[#1A2835] mt-1 whitespace-pre-wrap">{c.text}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[#5C7083] mb-4">Sin notas aun</p>
                        )}
                        {/* Add new comment */}
                        <div className="flex gap-2">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Escribe una nota..."
                            className="form-input flex-1 min-h-[40px] max-h-[120px] resize-y text-sm"
                            data-testid={`input-note-${task.id}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAddComment(task.id);
                              }
                            }}
                          />
                          <Button
                            className="btn-primary px-3 self-end"
                            onClick={() => handleAddComment(task.id)}
                            disabled={savingComment || !newComment.trim()}
                            data-testid={`btn-send-note-${task.id}`}
                          >
                            {savingComment ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* New Task Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nueva Tarea</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4" data-testid="task-form">
            <div>
              <Label className="form-label">Titulo *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="form-input" placeholder="Ej: Contactar cliente..." required data-testid="input-task-title" />
            </div>
            <div>
              <Label className="form-label">Descripcion</Label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-input min-h-[80px] resize-none" placeholder="Detalles de la tarea..." data-testid="input-task-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Prioridad</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                  <SelectTrigger data-testid="select-task-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="baja">Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Fecha Inicio</Label>
                <Input type="datetime-local" value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="form-input" data-testid="input-task-due-date" />
              </div>
              {isDirection() && (
                <div>
                  <Label className="form-label">Fecha Limite</Label>
                  <Input type="datetime-local" value={formData.due_date_end}
                    onChange={(e) => setFormData({ ...formData, due_date_end: e.target.value })}
                    className="form-input" data-testid="input-task-due-date-end" />
                  <p className="text-[10px] text-[#5C7083] mt-1">La tarea vencera al pasar esta fecha</p>
                </div>
              )}
            </div>
            {isDirection() && users.length > 0 && (
              <div>
                <Label className="form-label">Asignar a</Label>
                <Select value={formData.assigned_to || undefined} onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}>
                  <SelectTrigger data-testid="select-task-assignee"><SelectValue placeholder="Seleccionar usuario" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving || !formData.title} data-testid="btn-save-task">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Crear Tarea
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tareas;
